import { defineStore } from 'pinia';
import { useApplyError } from '~/store/useApplyError';
import { usePopup } from '~/store/usePopup';
import { useApp } from '~/store/useApp';
import axios from 'axios';
import { IApplyState, BaseApiResponse, ApplyResult } from '~/types/apply';

export const useApply = defineStore('apply', {
  state: (): IApplyState => {
    return {
      firstName: '',
      lastName: '',
      mail: '',
      areaCode: '972',
      areaCode2: '',
      mobile: '',
      confirm: false,
      notes: '',
      linkedIn: '',
      cv: [],
      additionalFiles: [],
      questions: [],
      gdpr: [],
      isLoading: false,
      isUserAgreedToPrivacyPolicy: false,
    };
  },
  actions: {
    changeStateHandleFactory<T>(key: keyof IApplyState) {
      const applyErrorStore = useApplyError();
      return (value: T) => {
        // @ts-ignore
        this[key] = value;
        // @ts-ignore
        applyErrorStore[key] = false;
      };
    },
    questionHandle(value: string, index: number) {
      const applyErrorStore = useApplyError();
      this.questions[index] = value;
      applyErrorStore.questions[index] = false;
    },
    gdprHandle(value: boolean, index: number) {
      const applyErrorStore = useApplyError();
      this.gdpr[index] = value;
      applyErrorStore.gdpr[index] = false;
    },
    async submitForm(
      recaptchaToken: string | null,
      shortlinkId: string,
    ): Promise<ApplyResult> {
      const applyErrorStore = useApplyError();
      const popupStore = usePopup();
      const config = useRuntimeConfig();
      const appStore = useApp();
      let isError = false;

      if (!this.firstName) {
        applyErrorStore.firstName = true;
        isError = true;
      }

      if (!this.lastName) {
        applyErrorStore.lastName = true;
        isError = true;
      }

      if (
        (appStore.apiData?.JobsiteSettings?.IsPhoneMandatory &&
          (!this.mobile || !this.areaCode)) ||
        this.mobile.length > 10
      ) {
        if (!this.mobile || this.mobile.length > 10) {
          applyErrorStore.mobile = true;
        }
        if (!this.areaCode) {
          applyErrorStore.areaCode = true;
        }

        if (!this.areaCode2) {
          applyErrorStore.areaCode2 = true;
        }

        isError = true;
      }

      // anystring@anystring.any
      if (
        (appStore.apiData?.JobsiteSettings?.IsEmailMandatory &&
          (!this.mail || !/\S+@\S+\.\S+/.test(this.mail))) ||
        (this.mail && !/\S+@\S+\.\S+/.test(this.mail))
      ) {
        applyErrorStore.mail = true;
        isError = true;
      }
      if (
        appStore.apiData?.JobsiteSettings?.IsCvMandatory &&
        (!this.cv || this.cv.length === 0)
      ) {
        applyErrorStore.cv = true;
        isError = true;
      }

      const jobsiteQuestions = appStore.apiData?.JobsiteQuestions ?? [];
      for (let index = 0; index < jobsiteQuestions.length; index++) {
        const question = jobsiteQuestions[index];
        if (!question?.IsMandatory) {
          continue;
        }

        const answer = this.questions[index];
        let isAnswered: boolean;
        if (question.AnswerType === 'Bool') {
          isAnswered = typeof answer === 'boolean';
        } else {
          isAnswered =
            answer !== null &&
            answer !== undefined &&
            String(answer).trim() !== '';
        }

        if (!isAnswered) {
          applyErrorStore.questions[index] = true;
          isError = true;
        }
      }

      const gdprItems = appStore.apiData?.JobInfo?.Gdpr ?? [];
      for (let index = 0; index < gdprItems.length; index++) {
        if (gdprItems[index]?.Required && !this.gdpr[index]) {
          applyErrorStore.gdpr[index] = true;
          isError = true;
        }
      }

      // Check privacy policy acceptance
      if (
        appStore.apiData?.PrivacyPolicyUrl &&
        !this.isUserAgreedToPrivacyPolicy
      ) {
        applyErrorStore.isUserAgreedToPrivacyPolicy = true;
        isError = true;
      }

      if (isError) {
        return;
      }

      if (!recaptchaToken) {
        popupStore.open = true;
        popupStore.modalText = 'Please use recaptcha!';
        popupStore.modalError = true;
        return;
      }

      const ip = await axios(
        `https://api.ipdata.co?api-key=${config.public.ipDataKey}&fields=ip`,
      )
        .then(({ data }) => data?.ip ?? '0.0.0.0')
        .catch((e) => {
          console.log(e.message);
          return '0.0.0.0';
        });

      const params = {
        toMail: appStore.apiData?.EmailBox ?? '',
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.mail,
        mobile: `${this.areaCode}-${this.areaCode2}-${this.mobile}`,
        linkedIn: this.linkedIn,
        notes: this.notes,
        questions: this.questions,
        gdpr: this.gdpr,
        questionData: appStore.apiData?.JobsiteQuestions,
        gdprData: appStore.apiData?.JobInfo.Gdpr,
        recaptchaToken,
        jobCode: appStore.apiData?.JobInfo.JobCode,
        supplierId: appStore.apiData?.SupplierId,
        shortlinkId,
        ip,
        isUserAgreedToPrivacyPolicy: this.isUserAgreedToPrivacyPolicy,
      };

      const body = new FormData();
      if (this.cv && this.cv.length > 0) {
        this.cv.forEach((file: File, index: number) => {
          body.append(`cv${index}`, file);
        });
      }

      if (this.additionalFiles && this.additionalFiles.length > 0) {
        this.additionalFiles.forEach((file: File, index: number) => {
          body.append(`additionalFile${index}`, file);
        });
      }

      this.isLoading = true;
      const sendApplyRes = await $fetch<BaseApiResponse>(
        config.public.sendMailApi,
        {
          method: 'POST',
          body,
          params,
        },
      );
      this.isLoading = false;

      if (sendApplyRes?.success) {
        popupStore.open = false;
        return {
          success: true,
          routeTo: '/thank-you',
          message: null,
        };
      } else {
        popupStore.modalError = true;
        popupStore.modalText = sendApplyRes?.message;
      }
      popupStore.open = true;
    },
  },
});
